
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

type FeedbackScoringPanelProps = {
  id: string;
  initialScoreMethod: 'value_effort' | 'rice';
  initialValueScore: number;
  initialEffortScore: number;
  initialReachScore: number;
  initialImpactScore: number;
  initialConfidenceScore: number;
  initialEffortRiceScore: number;
  onSaveScores: (
    id: string, 
    data: {
      score_method: 'value_effort' | 'rice',
      value_score?: number,
      effort_score?: number,
      reach_score?: number,
      impact_score?: number,
      confidence_score?: number,
      effort_rice_score?: number,
    }
  ) => void;
};

const FeedbackScoringPanel: React.FC<FeedbackScoringPanelProps> = ({
  id,
  initialScoreMethod,
  initialValueScore,
  initialEffortScore,
  initialReachScore,
  initialImpactScore,
  initialConfidenceScore,
  initialEffortRiceScore,
  onSaveScores,
}) => {
  const [scoreMethod, setScoreMethod] = useState<'value_effort' | 'rice'>(initialScoreMethod);
  const [valueScore, setValueScore] = useState<number>(initialValueScore);
  const [effortScore, setEffortScore] = useState<number>(initialEffortScore);
  const [reachScore, setReachScore] = useState<number>(initialReachScore);
  const [impactScore, setImpactScore] = useState<number>(initialImpactScore);
  const [confidenceScore, setConfidenceScore] = useState<number>(initialConfidenceScore);
  const [effortRiceScore, setEffortRiceScore] = useState<number>(initialEffortRiceScore);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Prepare data based on scoring method
      const scoreData = {
        score_method: scoreMethod,
        ...(scoreMethod === 'value_effort' 
          ? { value_score: valueScore, effort_score: effortScore } 
          : { 
              reach_score: reachScore, 
              impact_score: impactScore, 
              confidence_score: confidenceScore, 
              effort_rice_score: effortRiceScore 
            }
        )
      };
      
      await onSaveScores(id, scoreData);
    } catch (error) {
      console.error("Error saving scores:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-muted/30 border-dashed">
      <CardContent className="pt-6 space-y-6">
        {/* Score Method Selection */}
        <div className="space-y-3">
          <Label>Scoring Method</Label>
          <RadioGroup 
            value={scoreMethod} 
            onValueChange={(value) => setScoreMethod(value as 'value_effort' | 'rice')}
            className="flex flex-col space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="value_effort" id="value_effort_inline" />
              <Label htmlFor="value_effort_inline" className="cursor-pointer">Value/Effort</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="rice" id="rice_inline" />
              <Label htmlFor="rice_inline" className="cursor-pointer">RICE Score</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Value/Effort Scoring */}
        {scoreMethod === 'value_effort' && (
          <>
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Value (1-10)</Label>
                <span className="text-sm font-medium">{valueScore}</span>
              </div>
              <Slider
                value={[valueScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setValueScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How valuable is this to your customers?
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Effort (1-10)</Label>
                <span className="text-sm font-medium">{effortScore}</span>
              </div>
              <Slider
                value={[effortScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setEffortScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How much effort will it take to implement?
              </p>
            </div>
          </>
        )}

        {/* RICE Scoring */}
        {scoreMethod === 'rice' && (
          <>
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Reach (1-10)</Label>
                <span className="text-sm font-medium">{reachScore}</span>
              </div>
              <Slider
                value={[reachScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setReachScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How many users will this affect?
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Impact (1-10)</Label>
                <span className="text-sm font-medium">{impactScore}</span>
              </div>
              <Slider
                value={[impactScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setImpactScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How much will this improve the experience?
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Confidence (1-10)</Label>
                <span className="text-sm font-medium">{confidenceScore}</span>
              </div>
              <Slider
                value={[confidenceScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setConfidenceScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How confident are you in these estimates?
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Effort (1-10)</Label>
                <span className="text-sm font-medium">{effortRiceScore}</span>
              </div>
              <Slider
                value={[effortRiceScore]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => setEffortRiceScore(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                How much effort will this take to implement?
              </p>
            </div>
          </>
        )}

        <Button 
          className="w-full" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Scores"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default FeedbackScoringPanel;
